import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Skeleton,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalanceWallet as GLSyncIcon,
  AccountBalance as FinancialOperationsIcon,
  AccountBalance as TreasuryIcon,
  MonetizationOn as DebtCollectionIcon,
  ReceiptLong as AccountsPayableIcon,
  Settings as SettingsIcon,
  Settings as AppConfigIcon,
  Person as PersonIcon,
  ManageAccounts as GlobalUsersIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  WorkOutline as WorkflowsIcon,
  Groups as WorkforceOperationsIcon,
  BugReport as AnomalyDetectionIcon,
  Groups as TenantIcon,
  Engineering as TenantRoleIcon,
  Work as WorkflowDefinitionIcon,
  Tune as ConfigurationsIcon,
  DataArray as MasterDataIcon,
  Business as PropertyConfigIcon,
  CreditCard as CashReconIcon,
  CreditCard as CreditCardIcon,
  Category as DocumentTypeViewIcon,
  FolderOpen as DocumentsIcon,
  Description as DescriptionIcon,
  Help as HelpIcon,
  PrecisionManufacturing as OperationalAutomationIcon,
  // TrendingUp as InvestorHubIcon,
  Transform as DataExtractionIcon,
  // InfoOutlined as ComingSoonIcon,
  Storefront as VendorMasterIcon,
  AccountBalanceWallet as GlAccountMasterIcon,
  AccountBalance as BankMasterIcon,
  RequestQuote as BudgetCodesIcon,
  Handshake as ContractMasterIcon,
  AccountTree as ApWorkflowIcon,
  Hub as ExternalIntegrationIcon,
  History as AuditLogsIcon,
  LibraryBooks as ReferenceTemplatesIcon,
  Badge as PositionMasterIcon,
  CorporateFare as DepartmentMasterIcon,
  School as SkillsCertificationsIcon,
  Schedule as WorkforceOtStateIcon,
  BadgeOutlined as GlobalEmployeeIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserRights } from '../../context/UserRightsContext';
import { useAuth } from '../../context/AuthContext';
import NotificationPanel from './Notification';
import HarmonateLogo from './HarmonateLogo';
import { FC } from '../../constants/functionCodes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateSidebarPaperSx,
  harmonateSidebarLogoAreaSx,
  getHarmonateSidebarItemSx,
  getHarmonateSidebarSubItemSx,
  getHarmonateSidebarDisabledItemSx,
} from '../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../auth/serverIdentity';

// Menu configuration with function codes for permissions
const menuItems = [
  {
    text: 'Home',
    icon: <HomeIcon />,
    path: '/dashboard',
    functionCode: FC.HOME,
  },
  // Hidden from sidebar — route kept available
  // {
  //   text: 'Treasury',
  //   icon: <TreasuryIcon />,
  //   path: '/treasury',
  //   functionCode: FC.TREASURY,
  // },
  // {
  //   text: 'Anomaly Detection',
  //   icon: <AnomalyDetectionIcon />,
  //   path: '/anomaly-detection',
  //   functionCode: FC.ANOMALY_DETECTION,
  // },
  // {
  //   text: 'Debt Collection',
  //   icon: <DebtCollectionIcon />,
  //   path: '/debt-collection',
  //   functionCode: FC.DEBT_COLLECTION,
  // },
  // {
  //   text: 'Accounts Payable',
  //   icon: <AccountsPayableIcon />,
  //   path: '/accounts-payable-recovery',
  //   functionCode: FC.ACCOUNTS_PAYABLE,
  // },
];

const financialOperationsItems = [
  {
    text: 'Payment Intelligence',
    icon: <DebtCollectionIcon />,
    path: '/treasury-payment',
    functionCode: FC.PAYMENT_INTELLIGENCE,
    matchPrefix: '/treasury-payment',
  },
  {
    text: 'Cash Reconciliation',
    icon: <CashReconIcon />,
    path: '/cash-recon',
    functionCode: FC.CASH_RECONCILIATION,
  },
  {
    text: 'Merchant Data Hub',
    icon: <CreditCardIcon />,
    path: '/credit-card',
    functionCode: FC.MERCHANT_DATA_HUB,
    matchPrefixes: ['/credit-card', '/chargeback'],
  },
];

const operationalAutomationItems = [
  {
    text: 'Workforce Optimization',
    icon: <WorkforceOperationsIcon />,
    path: '/workforce-operations',
    functionCode: FC.WORKFORCE_OPTIMIZATION,
  },
  {
    text: 'PMS Reconciliation & Performance',
    icon: <DescriptionIcon />,
    path: '/hotel-sync/pms-gl-mapping',
    functionCode: FC.PMS_RECONCILIATION,
  },
];

// Hidden from sidebar — restore when Investor Hub is ready
// const investorHubItems = [
//   {
//     text: 'Future product coming soon',
//     icon: <ComingSoonIcon />,
//     comingSoon: true,
//     alwaysVisible: true,
//   },
// ];

const dataExtractionItems = [
  {
    text: 'Document Validation',
    icon: <WorkflowsIcon />,
    path: '/workflows/document-validation',
    matchPrefix: '/workflows/document-validation',
    functionCode: FC.DOCUMENT_VALIDATION,
  },
  {
    text: 'ETVL Engine',
    icon: <WorkflowsIcon />,
    path: '/workflows/etvl',
    matchPrefix: '/workflows/etvl',
    functionCode: FC.ETVL_ENGINE,
  },
  {
    text: 'GL Sync',
    icon: <GLSyncIcon />,
    path: '/gl-sync',
    functionCode: FC.GL_SYNC,
  },
];

const menuGroups = [
  {
    id: 'financial-operations',
    text: 'Financial Operations',
    icon: <FinancialOperationsIcon />,
    items: financialOperationsItems,
  },
  {
    id: 'operational-automation',
    text: 'Operational Automation',
    icon: <OperationalAutomationIcon />,
    items: operationalAutomationItems,
  },
  // Hidden from sidebar — restore when Investor Hub is ready
  // {
  //   id: 'investor-hub',
  //   text: 'Investor Hub',
  //   icon: <InvestorHubIcon />,
  //   items: investorHubItems,
  //   alwaysVisible: true,
  // },
  {
    id: 'data-extraction',
    text: 'Data Extraction and Transformation',
    icon: <DataExtractionIcon />,
    items: dataExtractionItems,
  },
];

// Labor / hospitality configuration (nested under Configurations)
const laborStandardsItems = [
  {
    text: 'Department Master',
    icon: <DepartmentMasterIcon />,
    path: '/configuration/department-master',
    functionCode: FC.DEPARTMENT_MASTER,
  },
  {
    text: 'Global Employee',
    icon: <GlobalEmployeeIcon />,
    path: '/configuration/global-employee',
    functionCode: FC.GLOBAL_EMPLOYEE,
  },
];

// Harmonate-only menus (top-level Harmonate menu)
const harmonateMenuItems = [
  {
    text: 'Tenant',
    icon: <TenantIcon />,
    path: '/tenant',
    functionCode: FC.TENANT,
  },
  {
    text: 'Role',
    icon: <TenantRoleIcon />,
    path: '/tenant-role',
    functionCode: FC.ROLE,
  },
  {
    text: 'Workflow Definition',
    icon: <WorkflowDefinitionIcon />,
    path: '/workflow-definition',
    functionCode: FC.WORKFLOW_DEFINITION,
  },
  {
    text: 'Workflow',
    icon: <ApWorkflowIcon />,
    path: '/configuration/ap-workflow-setup',
    functionCode: FC.WORKFLOW,
  },
  {
    text: 'Global Users',
    icon: <GlobalUsersIcon />,
    path: '/global-user',
    functionCode: FC.GLOBAL_USERS,
  },
  {
    text: 'Document Type',
    icon: <DocumentTypeViewIcon />,
    path: '/document-type-view',
    functionCode: FC.DOCUMENT_TYPE,
  },
  {
    text: 'Documents',
    icon: <DocumentsIcon />,
    path: '/documents',
    functionCode: FC.DOCUMENTS,
  },
  {
    text: 'External Integration',
    icon: <ExternalIntegrationIcon />,
    path: '/configuration/external-integration',
    functionCode: FC.EXTERNAL_INTEGRATION,
  },
  {
    text: 'Position Master',
    icon: <PositionMasterIcon />,
    path: '/configuration/position-master',
    functionCode: FC.POSITION_MASTER,
  },
  {
    text: 'Skills / Certifications',
    icon: <SkillsCertificationsIcon />,
    path: '/configuration/skills-certifications',
    functionCode: FC.SKILLS_CERTIFICATIONS,
  },
  {
    text: 'Workforce OT State',
    icon: <WorkforceOtStateIcon />,
    path: '/configuration/workforce-ot-state',
    functionCode: FC.WORKFORCE_OT_STATE,
  },
  {
    text: 'Audit Logs',
    icon: <AuditLogsIcon />,
    path: '/harmonate/audit-logs',
    functionCode: FC.AUDIT_LOGS,
  },
];

// Configuration sub-menu items with permissions
const configurationItems = [
  { 
    text: 'Tenant Workflow', 
    icon: <WorkflowsIcon />, 
    path: '/tenant-workflow',
    functionCode: FC.TENANT_WORKFLOW,
  },
  { 
    text: 'Users', 
    icon: <PersonIcon />, 
    path: '/user',
    functionCode: FC.USERS,
  },
  { 
    text: 'Master Data', 
    icon: <MasterDataIcon />, 
    path: '/master-data',
    functionCode: FC.MASTER_DATA,
  },
  { 
    text: 'Property', 
    icon: <PropertyConfigIcon />, 
    path: '/configuration/property',
    functionCode: FC.PROPERTY,
  },
  {
    text: 'Property Group',
    icon: <PropertyConfigIcon />,
    path: '/configuration/property-group',
    functionCode: FC.PROPERTY_GROUP,
  },
  {
    text: 'Vendor Master',
    icon: <VendorMasterIcon />,
    path: '/configuration/vendor-master',
    functionCode: FC.VENDOR_MASTER,
  },
  // Hidden from menubar — page/route still available if needed
  // {
  //   text: 'Business Unit Master',
  //   icon: <AccountsPayableIcon />,
  //   path: '/configuration/business-unit-master',
  //   functionCode: FC.ACCOUNTS_PAYABLE,
  // },
  {
    text: 'GL Account Master',
    icon: <GlAccountMasterIcon />,
    path: '/configuration/gl-account-master',
    functionCode: FC.GL_ACCOUNT_MASTER,
  },
  {
    text: 'Bank Master',
    icon: <BankMasterIcon />,
    path: '/configuration/bank-master',
    functionCode: FC.BANK_MASTER,
  },
  {
    text: 'Budget / Revenue Codes',
    icon: <BudgetCodesIcon />,
    path: '/configuration/budget-codes',
    functionCode: FC.BUDGET_REVENUE_CODES,
  },
  {
    text: 'Contract Master',
    icon: <ContractMasterIcon />,
    path: '/configuration/contract-master',
    functionCode: FC.CONTRACT_MASTER,
  },
  { 
    text: 'App Config', 
    icon: <AppConfigIcon />, 
    path: '/app-config',
    functionCode: FC.APP_CONFIG,
  },
  {
    text: 'Notification Configuration',
    icon: <NotificationsIcon />,
    path: '/configuration/notification-rules',
    functionCode: FC.NOTIFICATION_CONFIGURATION,
  },
  { 
    text: 'Reference Templates', 
    icon: <ReferenceTemplatesIcon />, 
    path: '/reference-templates',
    functionCode: FC.REFERENCE_TEMPLATES,
  },
  {
    text: 'Audit Logs',
    icon: <AuditLogsIcon />,
    path: '/user-logs',
    functionCode: FC.AUDIT_LOGS,
  },
];

const collapsedWidth = 64;
const expandedWidth = 280;

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [configurationsOpen, setConfigurationsOpen] = useState(false);
  const [laborStandardsOpen, setLaborStandardsOpen] = useState(false);
  const [harmonateMenuOpen, setHarmonateMenuOpen] = useState(false);
  const [openMenuGroups, setOpenMenuGroups] = useState({});
  const hoverTimeoutRef = useRef(null);

  // Get user rights context
  const { canAccessMenu, loading: rightsLoading } = useUserRights();
  
  // Check if user is harmonate user
  const isHarmonateUser = isServerHarmonateUser();
  
  // Get tenant name from localStorage
  const tenantName = localStorage.getItem('tenant_name') || 'Unknown Tenant';

  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    const userId = localStorage.getItem('user_id');
    const token = localStorage.getItem('token');
    if (!tenantId || !userId || !token) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/notifications/unread-count?tenant_id=${tenantId}&user_id=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setUnreadCount(Number(data?.count) || 0);
      } catch {
        if (!cancelled) setUnreadCount(0);
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [location.pathname]);

  // Disable flags for notifications and settings
  const isNotificationsDisabled = false;
  const isSettingsDisabled = true;

  const handleNavigation = (path, isExternal = false) => {
    if (!path) return;
    if (isExternal) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      // Check if we're already on this path
      if (location.pathname === path) {
        // Force a reload of the current route by navigating to a temporary path first
        // then immediately back to the target path, or use window.location for full reload
        // This ensures the page resets even when clicking the same menu item
        window.location.href = path;
      } else {
        navigate(path);
      }
    }
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Set timeout to expand sidebar after 1 seconds
    hoverTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
      hoverTimeoutRef.current = null;
    }, 1000);
  };

  const handleMouseLeave = () => {
    // Clear timeout if user moves away before 2 seconds
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsExpanded(false);
    if (!mobileOpen) {
      setConfigurationsOpen(false);
      setLaborStandardsOpen(false);
      setHarmonateMenuOpen(false);
      setOpenMenuGroups({});
    }
  };

  const toggleNotifications = () => {
    handleNavigation('/notifications');
  };

  const toggleConfigurations = () => {
    setConfigurationsOpen(!configurationsOpen);
  };

  const toggleLaborStandards = () => {
    setLaborStandardsOpen(!laborStandardsOpen);
  };

  const toggleHarmonateMenu = () => {
    setHarmonateMenuOpen(!harmonateMenuOpen);
  };

  const toggleMenuGroup = (groupId) => {
    setOpenMenuGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const isMenuItemActive = (item) => {
    if (item.isExternal || item.comingSoon || !item.path) return false;
    const prefixes = item.matchPrefixes?.length
      ? item.matchPrefixes
      : (item.matchPrefix ? [item.matchPrefix] : null);
    if (prefixes) {
      return prefixes.some(
        (prefix) =>
          location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
      );
    }
    return location.pathname === item.path;
  };

  const { logout } = useAuth();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const canShowMenuItem = (item) => {
    if (item.alwaysVisible || item.comingSoon) return true;
    if (isHarmonateUser) return true;
    if (rightsLoading) return false;
    if (item.functionCodes?.length) {
      return item.functionCodes.some((code) => canAccessMenu(code));
    }
    if (item.functionCode) {
      return canAccessMenu(item.functionCode);
    }
    return true;
  };

  // Filter menu items based on user rights
  const getVisibleMenuItems = () => {
    if (isHarmonateUser) {
      return menuItems;
    }
    if (rightsLoading) {
      return [];
    }
    return menuItems.filter(canShowMenuItem);
  };

  const getVisibleGroupItems = (items) => {
    if (isHarmonateUser) {
      return items;
    }
    if (rightsLoading) {
      return [];
    }
    return items.filter(canShowMenuItem);
  };

  // Filter configuration items based on user rights
  const getVisibleConfigurationItems = () => {
    // HARMONATE USERS: Show all configuration items
    if (isHarmonateUser) {
      return configurationItems;
    }

    // NON-HARMONATE USERS: Check permissions
    if (rightsLoading) {
      return [];
    }

    return configurationItems.filter(item => {
      if (item.functionCode) {
        const hasAccess = canAccessMenu(item.functionCode);
        console.log(`Config ${item.text} (${item.functionCode}): ${hasAccess}`);
        return hasAccess;
      }
      // Show by default if no permission defined
      return true;
    });
  };

  const getVisibleLaborStandardsItems = () => {
    if (isHarmonateUser) {
      return laborStandardsItems;
    }
    if (rightsLoading) {
      return [];
    }
    return laborStandardsItems.filter((item) => {
      if (item.functionCode) {
        return canAccessMenu(item.functionCode);
      }
      return true;
    });
  };

  const getVisibleHarmonateMenuItems = () => {
    if (!isHarmonateUser) {
      return [];
    }
    return harmonateMenuItems;
  };

  const visibleMenuItems = getVisibleMenuItems();
  const visibleMenuGroups = menuGroups
    .map((group) => ({
      ...group,
      items: getVisibleGroupItems(group.items),
    }))
    .filter((group) => group.alwaysVisible || group.items.length > 0);
  const visibleConfigurationItems = getVisibleConfigurationItems();
  const visibleLaborStandardsItems = getVisibleLaborStandardsItems();
  const visibleHarmonateMenuItems = getVisibleHarmonateMenuItems();

  const isLaborStandardsActive = visibleLaborStandardsItems.some(
    (item) => location.pathname === item.path
  );
  const isHarmonateMenuActive = visibleHarmonateMenuItems.some(
    (item) => location.pathname === item.path
  );
  const isConfigurationActive =
    visibleConfigurationItems.some((item) => location.pathname === item.path) ||
    isLaborStandardsActive;

  useEffect(() => {
    if (isLaborStandardsActive) {
      setConfigurationsOpen(true);
      setLaborStandardsOpen(true);
    }
  }, [isLaborStandardsActive]);

  useEffect(() => {
    if (isHarmonateMenuActive) {
      setHarmonateMenuOpen(true);
    }
  }, [isHarmonateMenuActive]);

  useEffect(() => {
    setOpenMenuGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      visibleMenuGroups.forEach((group) => {
        const isActive = group.items.some((item) => isMenuItemActive(item));
        if (isActive && !next[group.id]) {
          next[group.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  // Cleanup hover timeout on component unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const buttonStyle = useMemo(() => getHarmonateSidebarItemSx(isExpanded), [isExpanded]);
  const subButtonStyle = useMemo(() => getHarmonateSidebarSubItemSx(), []);
  const disabledButtonStyle = useMemo(() => getHarmonateSidebarDisabledItemSx(isExpanded), [isExpanded]);

  const drawerPaperSx = {
    boxSizing: 'border-box',
    ...harmonateSidebarPaperSx,
  };

  // Render skeleton for loading state
  const renderSkeletonItems = (count = 5) => {
    return Array.from({ length: count }, (_, index) => (
      <ListItem key={`skeleton-${index}`} disablePadding>
        <ListItemButton sx={buttonStyle}>
          <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 2 : 'auto', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemIcon>
          {isExpanded && (
            <ListItemText>
              <Skeleton variant="text" width={100} height={20} />
            </ListItemText>
          )}
        </ListItemButton>
      </ListItem>
    ));
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: ht.drawerBg }}>
      <Box
        sx={{
          ...harmonateSidebarLogoAreaSx,
          minHeight: isExpanded ? 72 : 52,
          py: isExpanded ? 1.5 : 0.75,
          px: isExpanded ? 1.5 : 0.75,
          justifyContent: isExpanded ? 'flex-start' : 'center',
        }}
      >
        {isExpanded ? (
          <HarmonateLogo
            variant="full"
            height={58}
            sx={{ width: '100%', maxWidth: 216, maxHeight: 64 }}
          />
        ) : (
          <HarmonateLogo variant="icon" height={32} />
        )}
      </Box>
      
      {/* Navigation Menu */}
      <List
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          py: 0,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { bgcolor: ht.drawerBg },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: ht.line,
            borderRadius: 3,
          },
        }}
      >
        {/* Show skeleton while loading rights for NON-harmonate users */}
        {!isHarmonateUser && rightsLoading ? (
          renderSkeletonItems()
        ) : (
          <>
            {/* Top-level items (Home) */}
            {visibleMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={isMenuItemActive(item)}
                  onClick={() => handleNavigation(item.path, item.isExternal)}
                  sx={buttonStyle}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isExpanded ? 2 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isMenuItemActive(item) ? 'medium' : 'normal',
                      variant: 'body2',
                      noWrap: true,
                    }}
                    sx={{
                      opacity: isExpanded ? 1 : 0,
                      display: isExpanded ? 'block' : 'none',
                      transition: 'opacity 0.3s',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

            {/* Grouped menus */}
            {visibleMenuGroups.map((group) => {
              const isGroupActive = group.items.some((item) => isMenuItemActive(item));
              const isGroupOpen = Boolean(openMenuGroups[group.id]);
              const firstNavigable = group.items.find((item) => item.path && !item.comingSoon);

              return (
                <React.Fragment key={group.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isGroupActive}
                      onClick={
                        isExpanded
                          ? () => toggleMenuGroup(group.id)
                          : () => handleNavigation(firstNavigable?.path)
                      }
                      sx={buttonStyle}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isExpanded ? 2 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {group.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={group.text}
                        primaryTypographyProps={{
                          fontWeight: isGroupActive ? 'medium' : 'normal',
                          variant: 'body2',
                          noWrap: true,
                        }}
                        sx={{
                          opacity: isExpanded ? 1 : 0,
                          display: isExpanded ? 'block' : 'none',
                          transition: 'opacity 0.3s',
                        }}
                      />
                      {isExpanded && (
                        <Box sx={{ ml: 'auto', opacity: isExpanded ? 1 : 0, transition: 'opacity 0.3s' }}>
                          {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={isGroupOpen && isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {group.items.map((item) => {
                        const active = isMenuItemActive(item);
                        return (
                          <ListItem key={item.text} disablePadding>
                            <ListItemButton
                              selected={active}
                              disabled={Boolean(item.comingSoon)}
                              onClick={() => {
                                if (item.comingSoon) return;
                                handleNavigation(item.path);
                              }}
                              sx={{
                                ...subButtonStyle,
                                ...(item.comingSoon
                                  ? {
                                      opacity: 0.7,
                                      '&.Mui-disabled': {
                                        opacity: 0.7,
                                      },
                                    }
                                  : {}),
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: 2,
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                  fontWeight: active ? 'medium' : 'normal',
                                  variant: 'body2',
                                  fontSize: '0.875rem',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* Configurations Menu - only show if there are visible config items */}
        {(visibleConfigurationItems.length > 0 || visibleLaborStandardsItems.length > 0) && (
          <>
            <ListItem disablePadding>
              <ListItemButton 
                selected={isConfigurationActive}
                onClick={isExpanded ? toggleConfigurations : () => handleNavigation(
                  visibleConfigurationItems[0]?.path || visibleLaborStandardsItems[0]?.path
                )}
                sx={buttonStyle}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isExpanded ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <ConfigurationsIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Configurations" 
                  primaryTypographyProps={{
                    fontWeight: isConfigurationActive ? 'medium' : 'normal',
                    variant: 'body2',
                    noWrap: true
                  }}
                  sx={{ 
                    opacity: isExpanded ? 1 : 0,
                    display: isExpanded ? 'block' : 'none',
                    transition: 'opacity 0.3s' 
                  }} 
                />
                {isExpanded && (
                  <Box sx={{ ml: 'auto', opacity: isExpanded ? 1 : 0, transition: 'opacity 0.3s' }}>
                    {configurationsOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>

            {/* Configuration Sub-menu */}
            <Collapse in={configurationsOpen && isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visibleConfigurationItems.map((item) => (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        selected={location.pathname === item.path}
                        onClick={() => handleNavigation(item.path)}
                        sx={subButtonStyle}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: 'center',
                            fontSize: '1.2rem'
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          primaryTypographyProps={{
                            fontWeight: location.pathname === item.path ? 'medium' : 'normal',
                            variant: 'body2',
                            fontSize: '0.875rem'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>

                    {item.path === '/configuration/property' && visibleLaborStandardsItems.length > 0 ? (
                      <>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={isLaborStandardsActive}
                            onClick={toggleLaborStandards}
                            sx={{
                              ...subButtonStyle,
                              pl: 4,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: 2,
                                justifyContent: 'center',
                                fontSize: '1.2rem',
                              }}
                            >
                              <WorkforceOperationsIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="Labor Standards"
                              primaryTypographyProps={{
                                fontWeight: isLaborStandardsActive ? 'medium' : 'normal',
                                variant: 'body2',
                                fontSize: '0.875rem',
                              }}
                            />
                            {laborStandardsOpen ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                        </ListItem>
                        <Collapse in={laborStandardsOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {visibleLaborStandardsItems.map((lsItem) => (
                              <ListItem key={lsItem.text} disablePadding>
                                <ListItemButton
                                  selected={location.pathname === lsItem.path}
                                  onClick={() => handleNavigation(lsItem.path)}
                                  sx={{
                                    ...subButtonStyle,
                                    pl: 6,
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      minWidth: 0,
                                      mr: 2,
                                      justifyContent: 'center',
                                      fontSize: '1.1rem',
                                    }}
                                  >
                                    {lsItem.icon}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={lsItem.text}
                                    primaryTypographyProps={{
                                      fontWeight:
                                        location.pathname === lsItem.path ? 'medium' : 'normal',
                                      variant: 'body2',
                                      fontSize: '0.8125rem',
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </>
                    ) : null}
                  </React.Fragment>
                ))}

                {!visibleConfigurationItems.some((item) => item.path === '/configuration/property') &&
                visibleLaborStandardsItems.length > 0 ? (
                  <>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isLaborStandardsActive}
                        onClick={toggleLaborStandards}
                        sx={subButtonStyle}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                          }}
                        >
                          <WorkforceOperationsIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Labor Standards"
                          primaryTypographyProps={{
                            fontWeight: isLaborStandardsActive ? 'medium' : 'normal',
                            variant: 'body2',
                            fontSize: '0.875rem',
                          }}
                        />
                        {laborStandardsOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={laborStandardsOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {visibleLaborStandardsItems.map((lsItem) => (
                          <ListItem key={lsItem.text} disablePadding>
                            <ListItemButton
                              selected={location.pathname === lsItem.path}
                              onClick={() => handleNavigation(lsItem.path)}
                              sx={{
                                ...subButtonStyle,
                                pl: 4,
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  mr: 2,
                                  justifyContent: 'center',
                                  fontSize: '1.1rem',
                                }}
                              >
                                {lsItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={lsItem.text}
                                primaryTypographyProps={{
                                  fontWeight:
                                    location.pathname === lsItem.path ? 'medium' : 'normal',
                                  variant: 'body2',
                                  fontSize: '0.8125rem',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : null}
              </List>
            </Collapse>
          </>
        )}

        {/* Harmonate Menu - Harmonate users only, top-level like Configurations */}
        {visibleHarmonateMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isHarmonateMenuActive}
                onClick={
                  isExpanded
                    ? toggleHarmonateMenu
                    : () => handleNavigation(visibleHarmonateMenuItems[0]?.path)
                }
                sx={buttonStyle}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isExpanded ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <TenantIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Harmonate"
                  primaryTypographyProps={{
                    fontWeight: isHarmonateMenuActive ? 'medium' : 'normal',
                    variant: 'body2',
                    noWrap: true,
                  }}
                  sx={{
                    opacity: isExpanded ? 1 : 0,
                    display: isExpanded ? 'block' : 'none',
                    transition: 'opacity 0.3s',
                  }}
                />
                {isExpanded && (
                  <Box sx={{ ml: 'auto', opacity: isExpanded ? 1 : 0, transition: 'opacity 0.3s' }}>
                    {harmonateMenuOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>

            <Collapse in={harmonateMenuOpen && isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visibleHarmonateMenuItems.map((hmItem) => (
                  <ListItem key={hmItem.text} disablePadding>
                    <ListItemButton
                      selected={location.pathname === hmItem.path}
                      onClick={() => handleNavigation(hmItem.path)}
                      sx={subButtonStyle}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                        }}
                      >
                        {hmItem.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={hmItem.text}
                        primaryTypographyProps={{
                          fontWeight: location.pathname === hmItem.path ? 'medium' : 'normal',
                          variant: 'body2',
                          fontSize: '0.875rem',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
      
      {/* Bottom action buttons - these are always visible */}
      <List sx={{ p: 0 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={toggleNotifications}
            selected={location.pathname === '/notifications'}
            sx={buttonStyle}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isExpanded ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText 
              primary="Notifications" 
              primaryTypographyProps={{ variant: 'body2' }}
              sx={{ 
                opacity: isExpanded ? 1 : 0,
                display: isExpanded ? 'block' : 'none',
                transition: 'opacity 0.3s' 
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Settings Button - DISABLED */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => !isSettingsDisabled && handleNavigation('/settings')}
            disabled={isSettingsDisabled}
            selected={location.pathname === '/settings'}
            sx={disabledButtonStyle}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isExpanded ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Settings" 
              primaryTypographyProps={{ variant: 'body2' }}
              sx={{ 
                opacity: isExpanded ? 1 : 0,
                display: isExpanded ? 'block' : 'none',
                transition: 'opacity 0.3s' 
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Help Button */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('https://nesfinancial.atlassian.net/servicedesk/customer/portals', true)}
            sx={buttonStyle}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isExpanded ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <HelpIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Help" 
              primaryTypographyProps={{ variant: 'body2' }}
              sx={{ 
                opacity: isExpanded ? 1 : 0,
                display: isExpanded ? 'block' : 'none',
                transition: 'opacity 0.3s' 
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Profile Button */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/profile')}
            selected={location.pathname === '/profile'}
            sx={buttonStyle}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isExpanded ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <PersonIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Profile" 
              primaryTypographyProps={{ variant: 'body2' }}
              sx={{ 
                opacity: isExpanded ? 1 : 0,
                display: isExpanded ? 'block' : 'none',
                transition: 'opacity 0.3s' 
              }}
            />
          </ListItemButton>
        </ListItem>
        
        {/* Logout Button */}
        <ListItem disablePadding>
          <Tooltip 
            title={`Logged in as: ${tenantName}`} 
            placement="right"
            arrow
          >
            <ListItemButton
              onClick={handleLogout}
              sx={buttonStyle}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isExpanded ? 2 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Logout" 
                primaryTypographyProps={{ variant: 'body2' }}
                sx={{ 
                  opacity: isExpanded ? 1 : 0,
                  display: isExpanded ? 'block' : 'none',
                  transition: 'opacity 0.3s' 
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{ 
          width: { sm: isExpanded ? expandedWidth : collapsedWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="sidebar navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              ...drawerPaperSx,
              width: expandedWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              ...drawerPaperSx,
              width: isExpanded ? expandedWidth : collapsedWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
              overflowY: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Notification Panel */}
      {notificationOpen && !isNotificationsDisabled && (
        <NotificationPanel open={notificationOpen} onClose={toggleNotifications} />
      )}
    </>
  );
};

export default Sidebar;